import { useMemo, useState } from 'react';
import { useLang } from 'hooks/lang';

import { actions, loaders, FullProjectIssue, ProjectIssueUserType } from 'entities';

import { Modal, Column, Button } from 'components/Blocks';
import { OwnerSelect } from 'components/Inputs';
import { UserPlusIcon } from 'components/Icons';

import { useUser } from 'hooks/user';
import { useConfirmation } from 'providers/Confirmation';
import { useProjectProfile } from 'pages/Projects/ProjectProfile/context';

import { sortByName, toUniqueList } from 'utils/array';
import { DEFAULT_NO_OWNER_USER } from 'utils/constants';

import { BaseProps } from '../types';

interface AddModalProps extends Pick<BaseProps, 'issue' | 'updateIssue'> {
  children?: never;
  type: ProjectIssueUserType | null;
  close: () => void;
}

export function AddModal({ issue, type, close, updateIssue }: AddModalProps) {
  const { t } = useLang();
  const user = useUser();

  const [selectedUser, setSelectedUser] = useState('');

  const { projectUsers } = useProjectProfile();

  const { data: sales, loading: loadingSales } = loaders.users.salesUsers.use({
    defaultValue: [],

    blockLoad: type !== 'SALES',

    parser: sortByName,
  });

  const { data: leaders, loading: loadingLeaders } = loaders.users.leaders.use({
    defaultValue: [],

    blockLoad: type !== 'EXECUTOR',

    params: {
      teams: toUniqueList(Object.values(projectUsers).map(({ team }) => team)),
    },
  });

  const issueUsers = useMemo(
    () => [...(issue?.salesInvolved || []), ...(issue?.executorsInvolved || [])],
    [issue],
  );

  const executorsAvailable = useMemo(() => {
    const projectPresence = Object.values(projectUsers).filter(
      ({ id }) => !leaders.find((user) => user.id === id),
    );

    return sortByName(
      [...projectPresence, ...leaders].filter(
        ({ id }) =>
          [user.id, DEFAULT_NO_OWNER_USER.id].every((badId) => badId !== id) &&
          !issueUsers.find((entry) => entry.user.id === id),
      ),
    );
  }, [projectUsers, user, issueUsers, leaders]);

  const salesAvailable = useMemo(() => {
    return sales.filter(
      ({ id }) =>
        [user.id, DEFAULT_NO_OWNER_USER.id].every((badId) => badId !== id) &&
        !issueUsers.find((entry) => entry.user.id === id),
    );
  }, [sales, issueUsers, user.id]);

  const executeClose = (): void => {
    close();
    setTimeout(() => setSelectedUser(''), 500);
  };

  const users = type === 'SALES' ? salesAvailable : executorsAvailable;

  const { requestConfirmation } = useConfirmation();

  const addUser = actions.projects.addUserToIssue.use({
    onSuccess: (added) => {
      const key = (
        type === 'SALES' ? 'salesInvolved' : 'executorsInvolved'
      ) as keyof FullProjectIssue;

      updateIssue({
        ...issue,

        [key]: [...(issue?.[key] as FullProjectIssue['salesInvolved']), added],
      } as FullProjectIssue);

      close();
    },
  });

  const executeAddition = (): void => {
    if (!selectedUser) return;

    requestConfirmation({
      title: t('add-user-with-name', {
        user: users.find(({ id }) => id === selectedUser)?.name,
      }) as string,

      text: t('issue-user-will-be-notified'),

      onConfirm: async () => {
        await addUser.execute({
          user: selectedUser,
          issue: issue!.id,
          project: issue!.project,
          type: type!,
        });
      },
    });
  };

  return (
    <Modal title={t('add-user')} isOpen={!!type} close={executeClose} className="phone:w-[90vw]">
      <Column gap="10" className="mt-4 px-8">
        <OwnerSelect
          value={selectedUser}
          onChange={setSelectedUser}
          users={type === 'SALES' ? salesAvailable : executorsAvailable}
          placeholder={t('select-user') as string}
          loading={loadingSales || loadingLeaders}
        />

        <Button onClick={executeAddition} disabled={!selectedUser}>
          <UserPlusIcon />
          {t('add')}
        </Button>
      </Column>
    </Modal>
  );
}
